/**
 * 🚀 FikoWebGPURenderer - WebGPU/WebGL Dual-Mode Renderer
 *
 * Three.js WebGPURenderer wrapper with automatic WebGL fallback.
 * Provides unified API for both renderer types.
 *
 * Performance Benefits:
 * - 10-30x draw call performance
 * - Unified compute pipeline
 * - Lower CPU overhead
 * - Better memory management
 */

import * as THREE from 'three';

// WebGPU Renderer ve ilgili modüller - Dynamic import ile yüklenecek
type WebGPURendererType = THREE.WebGLRenderer & {
  backend?: { device?: GPUDevice };
  init(): Promise<void>;
};

export interface FikoRendererOptions {
  canvas: HTMLCanvasElement;
  antialias?: boolean;
  alpha?: boolean;
  logarithmicDepthBuffer?: boolean;
  powerPreference?: 'high-performance' | 'low-power';
  precision?: 'highp' | 'mediump' | 'lowp';
  stencil?: boolean;
  preserveDrawingBuffer?: boolean;
  forceWebGL?: boolean;  // WebGPU'yu atla, direkt WebGL kullan
}

export interface FikoRendererStats {
  rendererType: 'webgpu' | 'webgl';
  fps: number;
  drawCalls: number;
  triangles: number;
  points: number;
  lines: number;
  textures: number;
  geometries: number;
  gpuMemory?: number;
}

/**
 * WebGPU/WebGL Dual-Mode Renderer
 */
export class FikoWebGPURenderer {
  private static instance: FikoWebGPURenderer | null = null;

  // Renderer instance (WebGPU veya WebGL)
  private _renderer: THREE.WebGLRenderer | WebGPURendererType | null = null;
  private _isWebGPU: boolean = false;
  private _isInitialized: boolean = false;
  private _initPromise: Promise<boolean> | null = null;

  // Options
  private options: FikoRendererOptions;

  // Stats
  private _lastStats: FikoRendererStats = {
    rendererType: 'webgl',
    fps: 0,
    drawCalls: 0,
    triangles: 0,
    points: 0,
    lines: 0,
    textures: 0,
    geometries: 0
  };

  // Performance monitoring
  private frameCount = 0;
  private lastTime = performance.now();

  private constructor(options: FikoRendererOptions) {
    this.options = options;
  }

  /**
   * Singleton factory - async initialization
   */
  static async create(options: FikoRendererOptions): Promise<FikoWebGPURenderer> {
    const instance = new FikoWebGPURenderer(options);
    await instance.init();
    return instance;
  }

  /**
   * Get existing instance (for component access)
   */
  static getInstance(): FikoWebGPURenderer | null {
    return FikoWebGPURenderer.instance;
  }

  /**
   * Initialize renderer (WebGPU first, fallback to WebGL)
   */
  async init(): Promise<boolean> {
    if (this._initPromise) {
      return this._initPromise;
    }

    this._initPromise = this._doInit();
    return this._initPromise;
  }

  private async _doInit(): Promise<boolean> {
    // Force WebGL if requested
    if (this.options.forceWebGL) {
      return this.initWebGL();
    }

    // Try WebGPU first
    if (await this.checkWebGPUSupport()) {
      const success = await this.initWebGPU();
      if (success) {
        FikoWebGPURenderer.instance = this;
        return true;
      }
    }

    // Fallback to WebGL
    const success = this.initWebGL();
    FikoWebGPURenderer.instance = this;
    return success;
  }

  /**
   * Check WebGPU browser support
   */
  private async checkWebGPUSupport(): Promise<boolean> {
    if (!navigator.gpu) {
      return false;
    }

    try {
      const adapter = await navigator.gpu.requestAdapter({
        powerPreference: this.options.powerPreference || 'high-performance'
      });

      if (!adapter) {
        return false;
      }

      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Initialize WebGPU Renderer
   */
  private async initWebGPU(): Promise<boolean> {
    try {
      // Dynamic import for WebGPU renderer from three/webgpu
      // Three.js 0.182+ exports WebGPU bundle via 'three/webgpu'
      // @ts-ignore - Three.js WebGPU modülü experimental
      const WebGPUModule = await import('three/webgpu');
      const WebGPURenderer = WebGPUModule.WebGPURenderer;

      if (!WebGPURenderer) {
        return false;
      }

      this._renderer = new WebGPURenderer({
        canvas: this.options.canvas,
        antialias: this.options.antialias ?? true,
        alpha: this.options.alpha ?? true,
        logarithmicDepthBuffer: this.options.logarithmicDepthBuffer ?? true,
        powerPreference: this.options.powerPreference ?? 'high-performance',
      }) as unknown as WebGPURendererType;

      // WebGPU renderer requires async init
      if (typeof (this._renderer as any).init === 'function') {
        await (this._renderer as any).init();
      }

      this._isWebGPU = true;
      this._isInitialized = true;
      this._lastStats.rendererType = 'webgpu';

      // Configure renderer
      this.configureRenderer();

      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Initialize WebGL Renderer (fallback)
   */
  private initWebGL(): boolean {
    try {
      this._renderer = new THREE.WebGLRenderer({
        canvas: this.options.canvas,
        antialias: this.options.antialias ?? true,
        alpha: this.options.alpha ?? true,
        logarithmicDepthBuffer: this.options.logarithmicDepthBuffer ?? true,
        powerPreference: this.options.powerPreference ?? 'high-performance',
        precision: this.options.precision ?? 'highp',
        stencil: this.options.stencil ?? true,
        preserveDrawingBuffer: this.options.preserveDrawingBuffer ?? false,
        failIfMajorPerformanceCaveat: false
      });

      this._isWebGPU = false;
      this._isInitialized = true;
      this._lastStats.rendererType = 'webgl';

      // Configure renderer
      this.configureRenderer();

      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Configure renderer settings (common for both)
   */
  private configureRenderer(): void {
    if (!this._renderer) return;

    // Common settings
    this._renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    this._renderer.outputColorSpace = THREE.SRGBColorSpace;
    this._renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this._renderer.toneMappingExposure = 1.0;

    // Shadow settings (WebGL specific)
    if (!this._isWebGPU && this._renderer instanceof THREE.WebGLRenderer) {
      this._renderer.shadowMap.enabled = true;
      this._renderer.shadowMap.type = THREE.PCFShadowMap;
    }
  }

  // ============================================================================
  // PUBLIC API - Renderer proxy methods
  // ============================================================================

  /**
   * Get underlying renderer
   */
  get renderer(): THREE.WebGLRenderer | WebGPURendererType {
    if (!this._renderer) {
      throw new Error('Renderer not initialized. Call init() first.');
    }
    return this._renderer;
  }

  /**
   * Is WebGPU active?
   */
  get isWebGPU(): boolean {
    return this._isWebGPU;
  }

  /**
   * Is initialized?
   */
  get isInitialized(): boolean {
    return this._isInitialized;
  }

  /**
   * Get GPU device (WebGPU only)
   */
  get gpuDevice(): GPUDevice | null {
    if (this._isWebGPU && this._renderer) {
      return (this._renderer as any).backend?.device || null;
    }
    return null;
  }

  /**
   * Render scene
   */
  render(scene: THREE.Scene, camera: THREE.Camera): void {
    if (!this._renderer) return;
    this._renderer.render(scene, camera);
    this.updateStats();
  }

  /**
   * Set size
   */
  setSize(width: number, height: number): void {
    if (!this._renderer) return;
    this._renderer.setSize(width, height);
  }

  /**
   * Set pixel ratio
   */
  setPixelRatio(ratio: number): void {
    if (!this._renderer) return;
    this._renderer.setPixelRatio(Math.min(ratio, 2));
  }

  /**
   * Get DOM element
   */
  get domElement(): HTMLCanvasElement {
    if (!this._renderer) {
      throw new Error('Renderer not initialized');
    }
    return this._renderer.domElement;
  }

  /**
   * Get WebGL context (WebGL mode only)
   */
  getContext(): WebGLRenderingContext | WebGL2RenderingContext | null {
    if (!this._isWebGPU && this._renderer instanceof THREE.WebGLRenderer) {
      return this._renderer.getContext();
    }
    return null;
  }

  /**
   * Get capabilities
   */
  get capabilities(): any {
    if (!this._renderer) return {};
    return (this._renderer as any).capabilities || {};
  }

  /**
   * Shadow map access
   */
  get shadowMap(): THREE.WebGLShadowMap | null {
    if (!this._isWebGPU && this._renderer instanceof THREE.WebGLRenderer) {
      return this._renderer.shadowMap;
    }
    return null;
  }

  /**
   * Set shadow map enabled
   */
  setShadowMapEnabled(enabled: boolean): void {
    if (!this._isWebGPU && this._renderer instanceof THREE.WebGLRenderer) {
      this._renderer.shadowMap.enabled = enabled;
    }
  }

  /**
   * Sort objects
   */
  set sortObjects(value: boolean) {
    if (this._renderer) {
      this._renderer.sortObjects = value;
    }
  }

  get sortObjects(): boolean {
    return this._renderer?.sortObjects ?? true;
  }

  /**
   * Output color space
   */
  set outputColorSpace(value: THREE.ColorSpace) {
    if (this._renderer) {
      this._renderer.outputColorSpace = value;
    }
  }

  /**
   * Tone mapping
   */
  set toneMapping(value: THREE.ToneMapping) {
    if (this._renderer) {
      this._renderer.toneMapping = value;
    }
  }

  set toneMappingExposure(value: number) {
    if (this._renderer) {
      this._renderer.toneMappingExposure = value;
    }
  }

  // ============================================================================
  // STATS & MONITORING
  // ============================================================================

  /**
   * Update performance stats
   */
  private updateStats(): void {
    this.frameCount++;
    const now = performance.now();
    const elapsed = now - this.lastTime;

    if (elapsed >= 1000) {
      this._lastStats.fps = Math.round((this.frameCount * 1000) / elapsed);
      this.frameCount = 0;
      this.lastTime = now;
    }

    // Get render info
    if (this._renderer) {
      const info = (this._renderer as any).info;
      if (info) {
        this._lastStats.drawCalls = info.render?.calls || 0;
        this._lastStats.triangles = info.render?.triangles || 0;
        this._lastStats.points = info.render?.points || 0;
        this._lastStats.lines = info.render?.lines || 0;
        this._lastStats.textures = info.memory?.textures || 0;
        this._lastStats.geometries = info.memory?.geometries || 0;
      }
    }
  }

  /**
   * Get current stats
   */
  getStats(): FikoRendererStats {
    return { ...this._lastStats };
  }

  /**
   * Get info object (Three.js compatible)
   */
  get info(): any {
    return (this._renderer as any)?.info || {};
  }

  // ============================================================================
  // CLEANUP
  // ============================================================================

  /**
   * Dispose renderer
   */
  dispose(): void {
    if (this._renderer) {
      this._renderer.dispose();
      this._renderer = null;
    }
    this._isInitialized = false;
    this._isWebGPU = false;

    if (FikoWebGPURenderer.instance === this) {
      FikoWebGPURenderer.instance = null;
    }
  }
}

/**
 * Quick check if WebGPU is available
 */
export async function isWebGPUAvailable(): Promise<boolean> {
  if (!navigator.gpu) return false;
  try {
    const adapter = await navigator.gpu.requestAdapter();
    return adapter !== null;
  } catch {
    return false;
  }
}

/**
 * Get WebGPU adapter info
 */
export async function getWebGPUInfo(): Promise<{
  available: boolean;
  vendor?: string;
  architecture?: string;
  device?: string;
  description?: string;
}> {
  if (!navigator.gpu) {
    return { available: false };
  }

  try {
    const adapter = await navigator.gpu.requestAdapter();
    if (!adapter) {
      return { available: false };
    }

    // requestAdapterInfo API değişkenlik gösterebilir
    // Bazı tarayıcılarda henüz mevcut değil
    let info: any = {};
    if (typeof (adapter as any).requestAdapterInfo === 'function') {
      info = await (adapter as any).requestAdapterInfo();
    }

    return {
      available: true,
      vendor: info?.vendor || 'unknown',
      architecture: info?.architecture || 'unknown',
      device: info?.device || 'unknown',
      description: info?.description || 'WebGPU Adapter'
    };
  } catch {
    return { available: false };
  }
}
